import { gql } from '@apollo/client';

import {
  WITH_COUNT_VARIABLES,
  WITH_COUNT_ARGUMENTS
} from '../../queries/withCount';

const ERRORS = `errors { path, message }`;

export const CORE_ASSET_FIELDS = `
  id
  year
  make
  model
  name
  vin
  lastSix
  stockNumber
  clientNumber
  condition
  category
  status
  miles
  hours
  subCategory
  engineMfr
  engineType
  transmission
  listPrice
  showListPrice
  axle
  sleeper
  suspension
  horsepower
  ratio
  wheelsRear
  wheelsFront
  wheelBase
  color
  mechanicalCondition
  expectedYieldPrice
  inspectionUrl
  inspectionCompleted
  nisInspectionUrl
  nisInspectionCompleted
  securedOn
  notes
  sellerNotes
  customLocation
  street
  city
  state
  postalCode { formatted, value }
  acquired
  acquiredOn
  acquiredPrice
  acquiredExpenses
  createdAt
  updatedAt
  soarrPhotoUrls
  cachedTitleStatus
  noTitleReminders
  feeType
  feeAmount
  consignmentMethod
  drivable
  customConsignmentAddress
  releaseYardContact
  releaseYardEmail
  featured
  newArrival
  showOnWeb
  removedAt
  uccExpiresOn
  buyerWireOn
  fundedOn
  titleApplicableAsset
`;

const ASSET_FIELDS = `
  ${CORE_ASSET_FIELDS}
  location {
    id
    street
    city
    state
    postalCode { formatted, value }
    phoneNumber { formatted, value }
  }
  consignmentLocation {
    id
    street
    city
    state
    postalCode { formatted, value }
    phoneNumber { formatted, value }
  }
  seller {
    id
    name
    releaseOption
    clientNumberLabelFull
    isEquiplinc
    feeAmount
    feeType
    inspectionFeeAmount
  }
  documents {
    id
    filename
    contentType
    url
  }
  photos {
    id
    filename
    contentType
    url
  }
  latestTitleLog {
    id
  }
  titleLogs {
    id
    asset {
      ${CORE_ASSET_FIELDS}
      titleReleaseWarning
      buyer { id, name, street, city, state, email, postalCode { formatted } }
    }
    upload { id, filename, contentType, url}
    linkedAttachment { id, name, url }
    titleNumber
    state
    notes
    status
    trackingShipper
    trackingNumber
    latestUpload { url }
    titleLogHistoryDeltas {
      id
      userDescription
      createdAt
      createdTimestamp
      resourceType
      resourceId
      fields {
        name
        oldValue
        newValue
      }
    }
    titleLogHistoryComments {
      id
      userDescription
      createdAt
      createdTimestamp
      body
    }
  }
  sellerDocuments {
    id
    filename
    contentType
    url
  }
  sellerDocumentNotes { id, attachmentId, body }
  status
  buyer { id, name, street, city, state, email, postalCode { formatted } }
  soldOn
  soldAmount
  preReleaseUrl
  releaseSentAt
  assetUsers {id, name, user {id, fullName}}
  assetSellerUsers {id, name, sellerUser {id, fullName}}
  lotNumber
  correctedLotNumber
  releaseYardEmail
  canBeDestroyed
  titleReleaseWarning
  equiplincContact {
    id
    fullName
  }
  reservePrice
  activeSaleAsset {
    lotNumber
    correctedLotNumber
    status
    reservePrice
    sale { saleOn, name }
  }
`

const ASSET_VARIABLES = `
  $sellerId: ID!
  $year: Int!
  $make: String!
  $model: String!
  $vin: String
  $stockNumber: String
  $clientNumber: String
  $mechanicalCondition: String
  $miles: Int
  $expectedYieldPrice: Decimal
  $reservePrice: Decimal
  $inspectionUrl: String
  $inspectionCompleted: Boolean
  $nisInspectionUrl: String
  $nisInspectionCompleted: Boolean
  $securedOn: ISO8601Date
  $acquired: String
  $acquiredOn: ISO8601Date
  $acquiredPrice: Decimal
  $acquiredExpenses: Decimal
  $locationId: ID
  $noTitleReminders: String
  $feeType: String
  $feeAmount: Decimal
  $customLocation: Boolean
  $street: String
  $city: String
  $state: String
  $postalCode: String
  $consignmentMethod: String
  $drivable: Boolean
  $consignmentLocationId: ID
  $notes: String
  $releaseYardContact: String
  $releaseYardEmail: String
  $equiplincContactId: ID
  $featured: Boolean
  $newArrival: Boolean
  $showListPrice: Boolean
  $listPrice: Decimal
  $showOnWeb: Boolean
  $uccExpiresOn: ISO8601Date
  $titleApplicableAsset: String
`;

const ASSET_ARGUMENTS = `
  sellerId: $sellerId
  year: $year
  make: $make
  model: $model
  vin: $vin
  stockNumber: $stockNumber
  clientNumber: $clientNumber
  mechanicalCondition: $mechanicalCondition
  miles: $miles
  expectedYieldPrice: $expectedYieldPrice
  reservePrice: $reservePrice
  inspectionUrl: $inspectionUrl
  inspectionCompleted: $inspectionCompleted
  nisInspectionUrl: $nisInspectionUrl
  nisInspectionCompleted: $nisInspectionCompleted
  securedOn: $securedOn
  acquired: $acquired
  acquiredOn: $acquiredOn
  acquiredPrice: $acquiredPrice
  acquiredExpenses: $acquiredExpenses
  locationId: $locationId
  noTitleReminders: $noTitleReminders
  feeType: $feeType
  feeAmount: $feeAmount
  customLocation: $customLocation
  street: $street
  city: $city
  state: $state
  postalCode: $postalCode
  consignmentMethod: $consignmentMethod
  drivable: $drivable
  consignmentLocationId: $consignmentLocationId
  notes: $notes
  releaseYardContact: $releaseYardContact
  releaseYardEmail: $releaseYardEmail
  equiplincContactId: $equiplincContactId
  featured: $featured
  newArrival: $newArrival
  showListPrice: $showListPrice
  listPrice: $listPrice
  showOnWeb: $showOnWeb
  uccExpiresOn: $uccExpiresOn
  titleApplicableAsset: $titleApplicableAsset
`;

const ASSET_FIELDS_AND_ERRORS = `
  asset {
    ${ASSET_FIELDS}
  }
  ${ERRORS}
`;

export const ASSETS_WITH_COUNT = gql`
  query (
    ${WITH_COUNT_VARIABLES}
    $sellerId: ID
    $buyerId: ID
    $saleId: ID
  ) {
    assetsWithCount(
      ${WITH_COUNT_ARGUMENTS}
      sellerId: $sellerId
      buyerId: $buyerId
      saleId: $saleId
    ) {
      entries {
        ${ASSET_FIELDS}
        soldSaleAsset {
          id
          sellerNet
          sellerAchOn
        }
      }
      count
    }
  }
`;

export const CREATE_ASSET = gql`
  mutation CreateAsset(
    ${ASSET_VARIABLES}
    $userIds: [ID!]
    $sellerUserIds: [ID!]
  ) {
    createAsset(
      ${ASSET_ARGUMENTS}
      userIds: $userIds
      sellerUserIds: $sellerUserIds
    ) {
      ${ASSET_FIELDS_AND_ERRORS}
    }
  }
`;

export const UPDATE_ASSET = gql`
  mutation UpdateAsset(
    $id: ID!
    ${ASSET_VARIABLES}
    $userIds: [ID!]
    $sellerUserIds: [ID!]
  ) {
    updateAsset(
      id: $id
      ${ASSET_ARGUMENTS}
      userIds: $userIds
      sellerUserIds: $sellerUserIds
    ) {
      ${ASSET_FIELDS_AND_ERRORS}
    }
  }
`;

export const UPDATE_SELLER_ASSET_TO_READY = gql`
  mutation UpdateAsset(
    $id: ID!
    $status: String
  ) {
    updateAsset(
      id: $id
      status: $status
    ) {
      ${ASSET_FIELDS_AND_ERRORS}
    }
  }
`;

export const UPDATE_ASSET_USERS = gql`
  mutation UpdateAsset(
    $id: ID!
    $userIds: [ID!]
    $sellerUserIds: [ID!]
  ) {
    updateAsset(
      id: $id
      userIds: $userIds
      sellerUserIds: $sellerUserIds
    ) {
      ${ASSET_FIELDS_AND_ERRORS}
    }
  }
`;

export const ASSET = gql`
  query Asset($id: ID!) {
    asset(id: $id) {
      ${ASSET_FIELDS}
      activeSaleAsset {
        lotNumber
        correctedLotNumber
        status
        reservePrice
        sale { saleOn, name }
      }
    }
  }
`;

export const HIDE_ASSET = gql`
  mutation HideAsset($id: ID!) {
    hideAsset(id: $id) {
      asset {
        id
      }
      errors {
        message
        path
      }
    }
  }
`;

export const DESTROY_ASSET = gql`
  mutation DestroyAsset($id: ID!) {
    destroyAsset(id: $id) {
      asset {
        id
      }
      errors {
        message
        path
      }
    }
  }
`;

export const REMOVE_ASSET = gql`
  mutation RemoveAsset($id: ID!, $remove: Boolean!) {
    removeAsset(id: $id, remove: $remove) {
      asset {
        id
      }
      errors {
        message
        path
      }
    }
  }
`;

export const SALE_UPDATE_ASSETS = gql`
  mutation SaleUpdateAssets(
    $saleId: ID!
    $assetInputs: [SaleAssetInput!]!
    $removeOthers: Boolean
    $moveFromOtherSales: Boolean
  ) {
    saleUpdateAssets(
      saleId: $saleId
      assetInputs: $assetInputs
      removeOthers: $removeOthers
      moveFromOtherSales: $moveFromOtherSales
    ) {
      sale { id, name }
      errors { path, message }
    }
  }
`;

export const IMPORT_ASSETS = gql`
  mutation ImportAssets(
    $sellerId: ID!
    $showOnWeb: Boolean
    $stockNumbers: [String!]!
    $userIds: [ID!]
    $sellerUserIds: [ID!]
  ) {
    importAssets(
      sellerId: $sellerId
      showOnWeb: $showOnWeb
      stockNumbers: $stockNumbers
      userIds: $userIds
      sellerUserIds: $sellerUserIds
    ) {
      assets {
        id
        stockNumber
        seller { id }
      }
      notFoundStockNumbers
    }
  }
`;

export const UPDATE_ASSET_RELEASE_SENT = gql`
  mutation UpdateAsset(
    $id: ID!
    $releaseSentAt: ISO8601DateTime
  ) {
    updateAsset(
      id: $id
      releaseSentAt: $releaseSentAt
    ) {
      ${ASSET_FIELDS_AND_ERRORS}
    }
  }
`;

export const UNWIND_SOLD_ASSET = gql`
  mutation UnwindSoldAsset($id: ID!) {
    unwindSoldAsset(id: $id) {
      saleAsset {
        id
        status
        unwoundAt
      }
    }
  }
`;

export const SELECT_OPTIONS = gql`
  query SelectOptions($sellerId: ID) {
    users(scopes: ["notifications"]) {
      id, fullName
    }
    sellerUsers(sellerId: $sellerId, scopes: ["notifications"]) {
      id, fullName
    }
  }
`

export const SELLER_USER_SELECT_OPTIONS = gql`
  query SelectOptions($sellerId: ID) {
    sellerUsers(sellerId: $sellerId, scopes: ["notifications"]) {
      id, fullName
    }
  }
`

// Slim field set for the all-assets index table; the full ASSET_FIELDS blob
// (documents, photos, title logs with history) is far too heavy for a list.
export const ASSETS_INDEX_WITH_COUNT = gql`
  query (
    ${WITH_COUNT_VARIABLES}
    $sellerId: ID
    $buyerId: ID
  ) {
    assetsWithCount(
      ${WITH_COUNT_ARGUMENTS}
      sellerId: $sellerId
      buyerId: $buyerId
    ) {
      entries {
        id
        vin
        stockNumber
        clientNumber
        year
        make
        model
        engineMfr
        transmission
        miles
        soldOn
        soldAmount
        cachedTitleStatus
        inspectionCompleted
        uccExpiresOn
        seller {
          id
          name
        }
      }
      count
    }
  }
`;

export const ASSET_FILTER_OPTIONS = gql`
  query NoUserLogAssetFilterOptions {
    selectOptions {
      assetMakes(includeSold: true)
      assetModels(includeSold: true)
      assetEngineMfrs(includeSold: true)
    }
    sellers {
      id
      fullDisplayName
    }
    buyers {
      id
      fullDisplayName
    }
  }
`;

export const UNUSED_STOCK_NUMBERS = gql`
  query UnusedStockNumbers {
    unusedStockNumbers {
      unitId
      stockNumber
      name
      vin
    }
  }
`;

export const VIN_TAKEN = gql`
  query VinTaken($vin: String!, $exceptAssetId: ID) {
    vinTaken(vin: $vin, exceptAssetId: $exceptAssetId)
  }
`;

export const LOT_NUMBER_SUGGESTION = gql`
  query NoUserLogLotNumberSuggestion(
    $saleId: ID!
    $assetId: ID!
    $prefix: String!
    $year: Int
  ) {
    lotNumberSuggestion(
      saleId: $saleId
      assetId: $assetId
      prefix: $prefix
      year: $year
    ) {
      lotNumberPrefix
      lotNumberYear
      lotNumberSuffix
      availableSuffixes
    }
  }
`;

export const SALE_ASSET_LOT_NUMBERS = gql`
  query SaleAssets(
    $saleId: ID
  ) {
    saleAssets(
      saleId: $saleId
    ) {
      id
      asset { id }
      lotNumberPrefix
      lotNumberYear
      lotNumberSuffix
    }
  }
`;

export const CONNECT_TO_SOARR = gql`
  mutation ConnectToSoarr($id: ID!) {
    connectToSoarr(id: $id) {
      ${ASSET_FIELDS_AND_ERRORS}
    }
  }
`;
